import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, map, tap } from 'rxjs';
import { environment } from "../../environments/environment";

export interface ConfigField {
  validationMail: string;
  autocomplete: string;
  editable: string;
  pattern: string;
  label: string;
  required: string;
  password: string;
  validationErrorMessage: string;
  name: string;
  options: string;
  value: string;
  order: string;
  primaryKey: string;
}

export interface FormConfig {
  config: ConfigField[];
  textConfig: any;
  onboardingType: string;
  logoUrl: string;
  group: string;
  idEmitter: string;
}

// Ajout d'une interface pour la structure de réponse de l'API
export interface DecodeUrlResponse extends FormConfig {
  // Autres propriétés potentielles de la réponse
}

@Injectable({
  providedIn: 'root'
})
export class ConsentService {
  private logoUrlSubject = new BehaviorSubject<string>('');
  private headerTitleSubject = new BehaviorSubject<string>('');
  private groupSubject = new BehaviorSubject<string>('');
  private formConfigSubject = new BehaviorSubject<FormConfig | null>(null);
  private formFieldsSubject = new BehaviorSubject<ConfigField[]>([]);
  private configLoaded = false;

  constructor(private http: HttpClient) {}

  isConfigLoaded(): boolean {
    return this.configLoaded;
  }

  setConfigLoaded(value: boolean): void {
    this.configLoaded = value;
  }

  // Méthodes pour obtenir les Observables
  getLogoUrlObservable(): Observable<string> {
    return this.logoUrlSubject.asObservable();
  }

  getHeaderTitleObservable(): Observable<string> {
    return this.headerTitleSubject.asObservable();
  }

  getGroupObservable(): Observable<string> {
    return this.groupSubject.asObservable();
  }

  getFormConfigObservable(): Observable<FormConfig | null> {
    return this.formConfigSubject.asObservable();
  }

  getFormFieldsObservable(): Observable<ConfigField[]> {
    return this.formFieldsSubject.asObservable();
  }

  // Méthodes pour mettre à jour les valeurs
  updateLogoUrl(url: string): void {
    this.logoUrlSubject.next(url);
  }

  updateGroup(group: string): void {
    this.groupSubject.next(group);
    localStorage.setItem('group', group);
  }

  updateHeaderTitle(title: string): void {
    this.headerTitleSubject.next(title);
  }

  updateFormConfig(config: FormConfig): void {
    this.formConfigSubject.next(config);
    if (config && config.config) {
      this.formFieldsSubject.next(config.config);
    }

    // Mise à jour des autres valeurs à partir de la configuration
    if (config && config.textConfig && config.textConfig.headerTitle) {
      this.updateHeaderTitle(config.textConfig.headerTitle);
    }

    if (config && config.logoUrl) {
      this.updateLogoUrl(config.logoUrl);
    }
  }

  // Méthode pour récupérer l'URL du logo
  getLogoUrl(group: string, logoName: string): Observable<any> {
    return this.http.post(environment.api_url + 'onboarding_v2', {
      group: group,
      file_name: logoName,
      action: 'getLogoUrl'
    }, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      })
    });
  }

  getConfig(group: string): Observable<FormConfig> {
    return this.http.post<FormConfig>(environment.api_url + 'onboarding_v2', {
      group: group,
      action: 'getConfig'
    }).pipe(
      tap(config => {
        this.updateFormConfig(config);
        // Stocke avec expiration de 15 minutes
        const expirationDate = new Date();
        expirationDate.setMinutes(expirationDate.getMinutes() + 1);

        localStorage.setItem(`formConfig_${group}`, JSON.stringify({
          config,
          expiration: expirationDate.getTime()
        }));
      })
    );
  }

  loadConfigFromLocal(group: string): boolean {
    const storedConfig = localStorage.getItem(`formConfig_${group}`);
    if (storedConfig) {
      try {
        const parsedData = JSON.parse(storedConfig);
        const now = new Date().getTime();

        if (parsedData.expiration && now < parsedData.expiration) {
          // Config valide (moins de 15 min)
          this.updateFormConfig(parsedData.config);
          return true;
        } else {
          // Expirée après 15 min, suppression du stockage local
          localStorage.removeItem(`formConfig_${group}`);
          return false;
        }
      } catch (error) {
        console.error('Erreur parsing config LocalStorage:', error);
        return false;
      }
    }
    return false;
  }

  // Méthode pour charger la configuration depuis un JSON statique
  loadLocalConfig(configJson: string): void {
    try {
      const config = JSON.parse(configJson) as FormConfig;
      this.updateFormConfig(config);
    } catch (error) {
      console.error('Erreur lors du parsing de la configuration:', error);
    }
  }

  // Méthode pour décoder l'URL - Correction du typage
  decodeUrl(data: string): Observable<DecodeUrlResponse> {
    return this.http.post<DecodeUrlResponse>(environment.api_url + 'onboarding_v2', {
      data: data,
      action: 'decode',
    }, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      })
    }).pipe(
      tap((response: DecodeUrlResponse) => {
        if (response && response.config) {
          this.updateFormConfig(response);
        }
      })
    );
  }

  decodeVerifyEmailUrl(data: string): Observable<any> {
    return this.http.post(environment.api_url + 'onboarding_v2', {
      data: data,
      action: 'decodeEmail',
    }, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      })
    });
  }

  // Méthode pour soumettre le consentement
  submitConsent(payload: any[], group: string): Observable<any> {
    return this.http.post(environment.api_url + 'onboarding_v2', {
      payload: payload,
      group: group,
      action: 'consent',
    }, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      })
    });
  }

  // Méthode utilitaire pour trouver le champ avec primaryKey=true
  getPrimaryKeyField(): ConfigField | undefined {
    const fields = this.formFieldsSubject.getValue();
    return fields.find(field => field.primaryKey === "true");
  }

  // Méthode utilitaire pour trouver le champ avec password=true
  getPasswordField(): ConfigField | undefined {
    const fields = this.formFieldsSubject.getValue();
    return fields.find(field => field.password === "true");
  }
}
